/* My garage on the storefront (spec: CRM docs/superpowers/specs/2026-10-08-garage-favorites-alerts-design.md).
   The only file that talks to the Garage app proxy (/apps/garage → CRM /api/garage). Shopify signs each
   proxied request with the signed-in customer; the browser holds no token. Signed out → Shopify sign-in,
   then back here with ?garage=… so the save completes. */
(() => {
  const API = '/apps/garage';
  const body = document.body;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const signedIn = body.dataset.cuCustomer === '1';
  const listeners = [];

  const status = document.createElement('p');
  status.className = 'cu-visually-hidden';
  status.setAttribute('role', 'status');
  body.append(status);
  const announce = (msg) => { status.textContent = ''; requestAnimationFrame(() => { status.textContent = msg; }); };

  async function api(path, payload) {
    const r = await fetch(API + path, payload
      ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) }
      : { headers: { Accept: 'application/json' } }).catch(() => { throw new Error('Something went wrong. Try again.'); });
    const j = await r.json().catch(() => ({}));
    if (r.status === 401) throw Object.assign(new Error('Sign in to use My garage'), { signIn: true });
    if (!r.ok) throw Object.assign(new Error(j.error === 'gone' ? 'This part is no longer available' : 'Something went wrong. Try again.'), { error: j.error });
    return j;
  }

  // New customer accounts: /customer_authentication/login takes a relative return_to.
  function signIn(resume) {
    const back = new URL(location.href);
    back.searchParams.set('garage', resume);
    const path = '/' + back.pathname.replace(/^\/+/, '') + back.search.replace(/%3A/gi, ':'); // always same-site relative
    location.assign(`/customer_authentication/login?return_to=${encodeURIComponent(path)}`);
  }

  const G = { api, signIn, announce, state: null, onState: (fn) => (G.state ? fn(G.state) : listeners.push(fn)) };
  window.CuGarage = G;

  /* ---------- Keyword search alerts (Spotlight, cu.js) ----------
     The page URL is what the CRM saves (it normalises it); the canonical form for a bare keyword
     search is /search?q=<terms>, the same URLSearchParams encoding the CRM uses. */
  const terms = (q) => String(q).trim().replace(/\s+/g, ' ');
  G.isSearchSaved = (q) => Boolean(G.state && G.state.searches.has(`/search?${new URLSearchParams({ q: terms(q) })}`));
  G.saveSearchAlert = async (q) => {
    const t = terms(q);
    if (!t) return { ok: false, message: '' };
    if (!signedIn) { signIn(`alert:${t}`); return { ok: false, redirecting: true }; }
    try {
      await api('/searches', { action: 'create', url: `/search?${new URLSearchParams({ q: t, type: 'product' })}`, label: `"${t}"`, cadence: 'instant' });
      G.state?.searches.add(`/search?${new URLSearchParams({ q: t })}`);
      announce(`Alerts are on for "${t}". Manage them in My garage.`);
      return { ok: true };
    } catch (e) {
      if (e.signIn) { signIn(`alert:${t}`); return { ok: false, redirecting: true }; }
      const message = e.error === 'too_broad'
        ? 'That search matches too many parts to alert on. Try a more specific word.'
        : 'Something went wrong. Try again.';
      announce(message);
      return { ok: false, message };
    }
  };

  /* ---------- Hearts ---------- */
  const hearts = (id) => document.querySelectorAll(`[data-cu-heart="${id}"]`);
  function paint(id, on) {
    hearts(id).forEach((b) => {
      b.setAttribute('aria-pressed', String(on));
      const t = b.querySelector('[data-cu-heart-text]');
      if (t) t.textContent = on ? 'Saved' : 'Save';
    });
  }
  async function toggle(id, on) {
    paint(id, on);
    hearts(id).forEach((b) => b.setAttribute('aria-busy', 'true'));
    try {
      await api('/favorites', { action: on ? 'add' : 'remove', productId: id });
      if (on) G.state?.favorites.add(id); else G.state?.favorites.delete(id);
      if (!on) document.dispatchEvent(new CustomEvent('cu-garage:removed', { detail: { id } }));
      announce(on ? 'Saved to My garage' : 'Removed from My garage');
    } catch (e) {
      paint(id, !on);
      if (e.signIn) return signIn(`fav:${id}`);
      announce(e.message);
    } finally {
      hearts(id).forEach((b) => b.removeAttribute('aria-busy'));
    }
  }
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-cu-heart]');
    if (!b || b.getAttribute('aria-busy') === 'true') return;
    e.preventDefault();
    const id = b.dataset.cuHeart;
    if (!signedIn) return signIn(`fav:${id}`);
    const on = b.getAttribute('aria-pressed') !== 'true';
    if (on && !reduceMotion) {
      b.classList.remove('is-pop'); void b.offsetWidth; b.classList.add('is-pop');
    }
    toggle(id, on);
  });

  /* ---------- State (signed in only), then finish whatever sign-in interrupted ---------- */
  const resume = new URL(location.href).searchParams.get('garage');
  if (resume) {
    const clean = new URL(location.href);
    clean.searchParams.delete('garage');
    history.replaceState(history.state, '', clean.pathname + clean.search + clean.hash);
  }
  /* ---------- Notify me (snippets/cu-facets) ---------- */
  const notifyBtn = document.querySelector('[data-cu-notify]');
  const dialog = document.getElementById('CuNotify');
  if (notifyBtn && dialog) {
    const form = dialog.querySelector('[data-cu-notify-form]');
    const err = dialog.querySelector('[data-cu-notify-error]');
    const text = notifyBtn.querySelector('[data-cu-notify-text]');
    const pageUrl = () => {
      const u = new URL(location.href);
      ['page', 'sort_by', 'section_id', 'garage'].forEach((k) => u.searchParams.delete(k));
      return u.pathname + u.search;
    };
    const markOn = () => {
      text.textContent = 'Alerts on';
      notifyBtn.dataset.cuNotifyOn = '';
      notifyBtn.removeAttribute('aria-haspopup');
      notifyBtn.removeAttribute('aria-controls');
    };
    const open = () => {
      if (!signedIn) return signIn('notify');
      if ('cuNotifyOn' in notifyBtn.dataset) { location.assign('/pages/my-garage'); return; }
      err.hidden = true;
      dialog.showModal();
      notifyBtn.setAttribute('aria-expanded', 'true');
    };
    const close = () => { dialog.close(); };
    dialog.addEventListener('close', () => { notifyBtn.setAttribute('aria-expanded', 'false'); notifyBtn.focus(); });
    notifyBtn.addEventListener('click', open);
    dialog.querySelector('[data-cu-notify-close]').addEventListener('click', close);
    dialog.addEventListener('click', (e) => { if (e.target === dialog) close(); });
    document.addEventListener('cu-garage:notify', open);
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const submit = form.querySelector('[type="submit"]');
      if (submit.getAttribute('aria-disabled') === 'true') return;
      const data = new FormData(form);
      submit.setAttribute('aria-disabled', 'true');
      try {
        await api('/searches', { action: 'create', url: pageUrl(), label: String(data.get('label')), cadence: String(data.get('cadence')) });
        markOn();
        close();
        announce('Alerts are on. Manage them in My garage.');
      } catch (ex) {
        if (ex.signIn) return signIn('notify');
        err.textContent = ex.error === 'unsupported' ? 'This page can’t be saved as an alert. Try a search or a category.'
          : ex.error === 'too_broad' ? 'That search matches too many parts to alert on. Add a filter or a more specific word.'
          : ex.message;
        err.hidden = false;
      } finally {
        submit.removeAttribute('aria-disabled');
      }
    });
    // "Alerts on" if this page is already saved; the CRM canonicalises the URL (see /state ?page=).
    G.onState((s) => { if (s.pageSaved) markOn(); });
  }

  /* ---------- My garage page (sections/cu-garage) ---------- */
  const page = document.querySelector('[data-cu-garage-page]');
  if (page) {
    const $ = (s) => page.querySelector(s);
    const usd = (n) => `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
    const showOut = () => { $('[data-cu-garage-out]').hidden = false; $('[data-cu-garage-in]').hidden = true; };
    const fail = (e) => {
      if (e.signIn) return showOut();
      const el = $('[data-cu-garage-error]');
      el.textContent = e.message;
      el.hidden = false;
    };
    const rowFocusable = (li) => li?.querySelector('a[href], button, select');
    // After a row is removed: focus the next row's control, else the previous, else the list's heading; refresh the empty state.
    function afterRemove(list, next, prev, heading, empty) {
      (rowFocusable(next) || rowFocusable(prev) || $(heading))?.focus();
      empty.hidden = !!list.querySelector('li');
      setCounts();
    }
    function removeRow(li, listSel, headingSel, emptySel) {
      const next = li.nextElementSibling;
      const prev = li.previousElementSibling;
      li.remove();
      afterRemove($(listSel), next, prev, headingSel, $(emptySel));
    }
    const removePart = (li) => removeRow(li, '[data-cu-garage-parts]', '#CuGaragePartsHead', '[data-cu-garage-parts-empty]');
    const clearError = () => { $('[data-cu-garage-error]').hidden = true; };
    const setCounts = () => {
      $('[data-cu-garage-count="parts"]').textContent = $('[data-cu-garage-parts]').children.length;
      $('[data-cu-garage-count="searches"]').textContent = $('[data-cu-garage-searches]').children.length;
    };
    // Card HTML comes from the theme's own ?view=card; if it can't load, show a plain title + remove control instead.
    function fallbackCard(it) {
      const wrap = document.createElement('div');
      wrap.className = 'cu-garage__fallback';
      if (it.handle) {
        const a = document.createElement('a');
        a.href = `/products/${encodeURIComponent(it.handle)}`;
        a.textContent = it.title;
        wrap.append(a);
      } else {
        const t = document.createElement('span');
        t.textContent = it.title;
        wrap.append(t);
      }
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'cu-heart cu-heart--pill';
      b.dataset.cuHeart = it.productId;
      b.setAttribute('aria-pressed', 'true');
      const txt = document.createElement('span');
      txt.className = 'cu-heart__text';
      txt.setAttribute('aria-hidden', 'true');
      txt.dataset.cuHeartText = '';
      txt.textContent = 'Saved';
      const name = document.createElement('span');
      name.className = 'cu-visually-hidden';
      name.textContent = `Save ${it.title} to My garage`;
      b.append(txt, name);
      wrap.append(b);
      return wrap;
    }
    async function renderParts() {
      const list = $('[data-cu-garage-parts]');
      try {
        const { items } = await api('/favorites');
        $('[data-cu-garage-parts-empty]').hidden = items.length > 0;
        const cards = await Promise.all(items.map(async (it) => {
          if (!it.handle) return '';
          try {
            const r = await fetch(`/products/${encodeURIComponent(it.handle)}?view=card`);
            return r.ok ? await r.text() : '';
          } catch (e) { return ''; }
        }));
        list.replaceChildren(...items.map((it, i) => {
          const li = document.createElement('li');
          li.className = 'cu-garage__part';
          li.dataset.productId = it.productId;
          if (cards[i]) li.innerHTML = cards[i]; else li.append(fallbackCard(it));
          if (it.sold) {
            li.classList.add('is-sold');
            const a = document.createElement('a');
            a.className = 'cu-garage__similar';
            a.href = `/search?type=product&q=${encodeURIComponent([it.vendor, it.productType].filter(Boolean).join(' ') || it.title)}`;
            a.textContent = 'Find similar';
            li.append(a);
          } else if (it.price !== null && it.price < it.savedPrice) {
            const b = document.createElement('span');
            b.className = 'cu-badge cu-badge--stock cu-garage__drop';
            b.textContent = `Dropped ${usd(it.savedPrice - it.price)}`;
            li.querySelector('.cu-pcard__media')?.append(b);
          }
          li.querySelectorAll('[data-cu-heart]').forEach((h) => h.setAttribute('aria-pressed', 'true'));
          return li;
        }));
      } finally {
        list.removeAttribute('aria-busy');
      }
    }
    function searchRow(s) {
      const li = document.createElement('li');
      li.className = 'cu-garage__search';
      li.innerHTML = `
        <div>
          <p class="cu-label cu-garage__search-kind" data-kind></p>
          <a class="cu-garage__label" href=""></a>
        </div>
        <div class="cu-garage__controls">
          <label class="cu-garage__cadence"><span class="cu-visually-hidden">How often for </span><span class="cu-visually-hidden" data-name></span>
            <select><option value="instant">As they list</option><option value="daily">Daily at 8am</option></select></label>
          <button type="button" class="cu-pill cu-pill--outline" data-pause></button>
          <button type="button" class="cu-pill cu-pill--outline" data-delete>Delete</button>
        </div>`;
      li.querySelector('[data-kind]').textContent = s.kind === 'collection' ? 'Category alert' : 'Search alert';
      const link = li.querySelector('a');
      link.href = /^\/(?![\/\\])/.test(s.pageUrl) ? s.pageUrl : '/';
      link.textContent = s.label;
      li.querySelector('[data-name]').textContent = s.label;
      const select = li.querySelector('select');
      select.value = s.cadence;
      let cadence = s.cadence;
      const pause = li.querySelector('[data-pause]');
      const paint = () => {
        pause.textContent = s.paused ? 'Resume' : 'Pause';
        pause.setAttribute('aria-label', `${s.paused ? 'Resume' : 'Pause'} alerts for ${s.label}`);
        li.classList.toggle('is-paused', s.paused);
      };
      paint();
      li.querySelector('[data-delete]').setAttribute('aria-label', `Delete ${s.label}`);
      select.addEventListener('change', () => api('/searches', { action: 'update', id: s.id, cadence: select.value })
        .then(() => { cadence = select.value; clearError(); announce(`${s.label}: ${cadence === 'daily' ? 'daily' : 'as they list'}`); })
        .catch((e) => { select.value = cadence; fail(e); }));
      pause.addEventListener('click', () => api('/searches', { action: 'update', id: s.id, paused: !s.paused })
        .then(() => { s.paused = !s.paused; paint(); clearError(); announce(`${s.label}: alerts ${s.paused ? 'paused' : 'resumed'}`); }).catch(fail));
      li.querySelector('[data-delete]').addEventListener('click', () => api('/searches', { action: 'delete', id: s.id })
        .then(() => {
          clearError();
          announce(`Deleted ${s.label}`);
          removeRow(li, '[data-cu-garage-searches]', '#CuGarageSearchesHead', '[data-cu-garage-searches-empty]');
        }).catch(fail));
      return li;
    }
    async function renderSearches() {
      const { items } = await api('/searches');
      $('[data-cu-garage-searches-empty]').hidden = items.length > 0;
      $('[data-cu-garage-searches]').replaceChildren(...items.map(searchRow));
    }
    if (!signedIn) showOut();
    else Promise.all([renderParts(), renderSearches()]).then(setCounts, fail);
    // Un-hearting removes the card only once the CRM has confirmed (toggle() fires this event).
    document.addEventListener('cu-garage:removed', (e) => {
      const li = [...page.querySelectorAll('.cu-garage__part')].find((x) => x.dataset.productId === String(e.detail.id));
      if (li) removePart(li);
    });
  }

  if (!signedIn) return;
  api(`/state?page=${encodeURIComponent(location.pathname + location.search)}`).then((s) => {
    G.state = { favorites: new Set(s.favorites), searches: new Set(s.searches), pageSaved: Boolean(s.pageSaved) };
    G.state.favorites.forEach((id) => paint(id, true));
    listeners.splice(0).forEach((fn) => fn(G.state));
    const fav = /^fav:(\d+)$/.exec(resume || '');
    if (fav && !G.state.favorites.has(fav[1])) toggle(fav[1], true);
    if (resume === 'notify') document.dispatchEvent(new CustomEvent('cu-garage:notify'));
    const alertQ = /^alert:(.+)$/.exec(resume || '');
    if (alertQ && !G.isSearchSaved(alertQ[1])) G.saveSearchAlert(alertQ[1]);
  }).catch(() => {
    // Signed-in hint without a session (or the app is down): hearts fall back to sign-in on click.
    if (resume) announce('Couldn’t reach My garage. Try again.');
  });
})();
